import type { ICompany } from './company'
import type { IUserNotificationSettings } from './notification-settings'
import type { IMyWorkspace, IWorkspace, WorkspaceVerificationTypeEnum } from './workspace'

export interface UsersWorkspace {
  accessType: string
  isPrimary?: boolean
  role?: string
  status: string
  workspace: IMyWorkspace
}

export enum CreatedVia {
  ChaineWebApp = 'ChaineWebApp',
  Integration = 'Integration'
}

export enum MembershipStatus {
  ACTIVE = 'Active',
  INACTIVE = 'Inactive',
  INVITED = 'Invited'
}

export enum UserStatus {
  ACTIVE = 'Active',
  DEACTIVATED = 'Deactivated',
  REGISTERED = 'Registered'
}

export type AuthUserDetails = {
  emailOrPhone: string
  session?: string
  /**
   * The user's status (e.g. "Registered")
   */
  status?: UserStatus
  userID: string
}

export enum MembershipAccessType {
  ADMIN = 'Admin',
  DRIVER = 'Driver',
  MEMBER = 'Member',
  OWNER = 'Owner'
}

export enum UserRoleEnum {
  CarrierSalesRep = 'CarrierSalesRep',
  CustomerSalesRep = 'CustomerSalesRep',
  Dispatcher = 'Dispatcher',
  Driver = 'Driver',
  OwnerOperator = 'OwnerOperator',
  ShipperSalesRep = 'ShipperSalesRep'
}

/**
 * The return type for the "me" graphql query
 */
export interface MeReturnType {
  colorModePreference?: string

  countryCode?: string | null
  email?: string

  /**
   * The user's UUID
   */
  id: string
  isEmailVerified?: boolean
  isPhoneVerified?: boolean
  isSuper: boolean | null
  /**
   * The name of the user
   */
  name: string | null
  notificationSettings: IUserNotificationSettings | null

  phone?: string
  /**
   * The user's photo
   */
  photo: string | null
  /**
   * The user's primary membership
   */
  primaryMembership?: {
    /**
     * The user's membership access type of type {@link MembershipAccessType}
     */
    accessType: MembershipAccessType
    /**
     * The user's role at their primary workspace of type {@link UserRole}
     */
    role: UserRoleEnum
    status: MembershipStatus
    workspace: {
      /**
       * The display name of the workspace
       */
      displayName: string
      /**
       * DOT of the company
       */
      dot: string
      /**
       * The workspaceID
       */
      id: string
      /**
       * Boolean value that indicates if a workspace has gone through basic FMSCA verification
       */
      isVerified: boolean
      /**
       * The logo of the workspace used in Avatar
       */
      logo: string
      /**
       * MC number of the company
       */
      mcNumber: string

      /**
       * The type of verification {@link WorkspaceVerificationType}
       */
      workspaceVerificationType: WorkspaceVerificationTypeEnum | null

      /**
       * The unique workspacename (analogous to username for a user)
       */
      workspacename: string
    }
  }
  role: UserRoleEnum | null
  shipmentsPageSettings: {
    hideNeedsCarrierShipments: boolean
  }
  status: UserStatus
  userPage: {
    bio: string
    freightPreferences: string[]
  }
  /**
   * The user's unique username
   */
  username: string
}

interface IWorkspaceUser {
  /**
   * A member's access type (e.g. Member, Admin, Owner)
   */
  accessType: MembershipAccessType

  user: {
    /**
     *  The phone number's country code
     */
    countryCode?: string
    /**
     *  The member's email
     */
    email?: string
    /**
     *  The member's UUID
     */
    id: string
    /**
     *  The member's name
     */
    name?: string
    /**
     *  The member's phone number
     */
    phone?: string
    /**
     *  The member's photo
     */
    photo?: string
    /**
     *  The user's username
     */
    username: string
  } | null

  /**
   * The member's userID
   */
  userID: string

  /**
   * The workspace's UUID
   */
  workspaceID: string
}

export interface IWorkspaceMember extends IWorkspaceUser {
  /**
   *  A member's status (e.g. 'Active' or 'Inactive')
   */
  status: MembershipStatus
}

/**
 * Response from the get workspace invitations query
 */
export type TGetWorkspaceInvitationsReturnType = {
  accessType: MembershipAccessType
  expirationDate: string
  user: {
    countryCode: string
    email: string
    id: string
    name: string
    phone: string
    photo: string
  }
  userID: string
  workspaceID: string
}

export enum MembershipRole {
  CarrierSalesRep = 'CarrierSalesRep',
  CustomerSalesRep = 'CustomerSalesRep',
  Dispatcher = 'Dispatcher',
  Driver = 'Driver',
  OwnerOperator = 'OwnerOperator',
  ShipperSalesRep = 'ShipperSalesRep'
}

export interface TypeSafeMembership {
  accessType: MembershipAccessType
  role: MembershipRole
  status: MembershipStatus
  userID: string
  workspace: IMyWorkspace
  workspaceID: string
}

export interface IUserExistsReturnData {
  /**
   * The user's phone country code
   */
  countryCode: string | null
  email: string | null
  /**
   * The user's UUID
   */
  id: string
  memberships: TypeSafeMembership[] | []

  name: string | null
  /**
   * The user's phone number
   */
  phone: string | null
  photo: string | null
}

/**
 * The return type for the user page in the "user" graphql query
 */
export type UserPageReturnType = {
  /**
   * The user's bio info (a small summary or a short description about the user)
   */
  bio: string | null

  /**
   * The user's freight preferences (Ex: Dry Van, Reefer, Hazmat)
   */
  freightPreferences: string[]
}

/**
 * The return type for the "user" graphql query
 */
export type UserReturnType = {
  /**
   * User unique identifier
   */
  id: string

  /**
   * The name of the user
   */
  name: string

  /**
   * The user's photo
   */
  photo: string | null

  /**
   * The user's page
   */
  primaryMembership: {
    accessType: string | null
    isPrimary: boolean | null
    workspace: {
      company: ICompany
      displayName: string | null
      isVerified: boolean
      logo: string | null
    }
  }

  /**
   * The user's role (e.g. "Carrier Sales Rep")
   */
  role: UserRoleEnum | null

  /**
   * The user's page
   */
  userPage: UserPageReturnType

  /**
   * The user's unique username
   */
  username: string
}

export interface IMembership {
  accessType?: MembershipAccessType
  role?: MembershipRole | UserRoleEnum
  status?: MembershipStatus
  userID?: string
  workspace?: IWorkspace
  workspaceID?: string
}

export interface IPhoneNumberInfo {
  canReceiveSMS: boolean
  carrierName?: string
  carrierType?: string
  carrierTypeDescription?: string
  isValid: boolean
}

export interface UserDetails {
  countryCode?: string
  displayName?: string
  email?: string
  id: string
  name?: string
  online?: boolean
  phone?: string
  phoneNumberInfo?: IPhoneNumberInfo
  photo?: string
  role?: UserRoleEnum
  username?: string
  workspaceID?: string
}

export interface IUserWithPrimaryWorkspace {
  email: string | null
  id: string
  name: string
  photo: string
  primaryMembership: {
    accessType: string | null
    isPrimary: boolean | null
    workspace: { displayName: string; isVerified: boolean; logo: string | null }
  } | null
  role: UserRoleEnum | null
  username: string
}

export interface UserAvatarProps {
  showOnlineBadge?: boolean
  showTooltip?: boolean
  size?: 'xs' | 'sm' | 'lg' | 'md' | 'xl' | '2xl' | '2xs' | null | undefined
  user: {
    email?: string
    id?: string
    name?: string
    online?: boolean
    phone?: string
    photo?: string | null
  }
  variant?: 'square' | 'basic'
  workspace?: IUserWorkspace
}

export interface IUserInfo {
  email?: string
  id?: string
  isVerified?: boolean
  name?: string
  online?: boolean
  phone?: string
  photo?: string | null
  role?: UserRoleEnum | null
}

export interface IUserWorkspace {
  id?: string
  isVerified?: boolean
  name?: string
}
